import { useEffect, useState } from "react";

import api from "../api/client";

type Customer = { id: number; name: string };
type Entry = { id?: number; role: string; hourly_rate_cents: number; sort_index: number };
type RateCard = {
  id: number;
  customer_id: number | null;
  name: string;
  is_default: boolean;
  active: boolean;
  notes: string | null;
  entries: Entry[];
};

const fmt = (cents: number) =>
  `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

type Draft = {
  name: string;
  customer_id: number | "";
  is_default: boolean;
  active: boolean;
  notes: string;
  entries: Entry[];
};

const emptyDraft = (): Draft => ({
  name: "",
  customer_id: "",
  is_default: false,
  active: true,
  notes: "",
  entries: [{ role: "Tier 1", hourly_rate_cents: 12500, sort_index: 0 }],
});

export default function PSARateCards() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [cards, setCards] = useState<RateCard[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [err, setErr] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    api.get("/api/v1/customers").then((r) => setCustomers(r.data || []));
  }, []);

  const load = async () => {
    setErr("");
    try {
      const r = await api.get("/api/v1/psa/rate-cards");
      setCards(r.data || []);
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Failed to load rate cards");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const cName = (id: number | null) =>
    id == null ? "Global (house) card" : customers.find((c) => c.id === id)?.name || `customer ${id}`;

  const create = async () => {
    if (!draft) return;
    setWorking(true);
    setErr("");
    try {
      await api.post("/api/v1/psa/rate-cards", {
        name: draft.name,
        customer_id: draft.customer_id === "" ? null : draft.customer_id,
        is_default: draft.is_default,
        active: draft.active,
        notes: draft.notes || undefined,
        entries: draft.entries.map((e, i) => ({ ...e, sort_index: i })),
      });
      setDraft(null);
      load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Create failed");
    } finally {
      setWorking(false);
    }
  };

  const saveEntries = async (card: RateCard, entries: Entry[]) => {
    setWorking(true);
    setErr("");
    try {
      await api.put(
        `/api/v1/psa/rate-cards/${card.id}/entries`,
        entries.map((e, i) => ({
          role: e.role,
          hourly_rate_cents: e.hourly_rate_cents,
          sort_index: i,
        })),
      );
      load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Save failed");
    } finally {
      setWorking(false);
    }
  };

  const remove = async (card: RateCard) => {
    if (!window.confirm(`Delete rate card "${card.name}"?`)) return;
    await api.delete(`/api/v1/psa/rate-cards/${card.id}`);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">PSA — Rate cards</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Role → hourly-rate schedules. A customer card overrides the global house card; the
            matched rate is snapshotted onto each time entry when work is logged, so editing a
            card never restates past time.
          </p>
        </div>
        <button
          onClick={() => setDraft(emptyDraft())}
          className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:opacity-90"
        >
          New rate card
        </button>
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">{err}</div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {cards.map((card) => (
          <CardEditor
            key={card.id}
            card={card}
            label={cName(card.customer_id)}
            working={working}
            onSave={(entries) => saveEntries(card, entries)}
            onDelete={() => remove(card)}
          />
        ))}
        {cards.length === 0 && (
          <div className="text-sm text-gray-500 dark:text-gray-400 italic">No rate cards yet — create one to start billing by role.</div>
        )}
      </div>

      {draft && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">New rate card</h2>
            <div className="space-y-3 text-sm">
              <label className="block">
                <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Name</span>
                <input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
                  placeholder="Standard 2026 schedule"
                />
              </label>
              <label className="block">
                <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Scope</span>
                <select
                  value={draft.customer_id}
                  onChange={(e) =>
                    setDraft({ ...draft, customer_id: e.target.value === "" ? "" : Number(e.target.value) })
                  }
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
                >
                  <option value="">Global (house) card</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={draft.is_default}
                  onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })}
                />
                Default card for this scope
              </label>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Roles &amp; rates</span>
                  <button
                    onClick={() =>
                      setDraft({
                        ...draft,
                        entries: [
                          ...draft.entries,
                          { role: "", hourly_rate_cents: 0, sort_index: draft.entries.length },
                        ],
                      })
                    }
                    className="text-xs text-brand dark:text-brand-light"
                  >
                    + Add role
                  </button>
                </div>
                {draft.entries.map((en, i) => (
                  <div key={i} className="flex gap-2 mb-1">
                    <input
                      value={en.role}
                      onChange={(e) => {
                        const entries = [...draft.entries];
                        entries[i] = { ...en, role: e.target.value };
                        setDraft({ ...draft, entries });
                      }}
                      placeholder="Role (e.g. Tier 1)"
                      className="flex-1 border dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-900 dark:text-gray-100"
                    />
                    <input
                      type="number"
                      step="0.01"
                      value={(en.hourly_rate_cents / 100).toString()}
                      onChange={(e) => {
                        const entries = [...draft.entries];
                        entries[i] = {
                          ...en,
                          hourly_rate_cents: Math.round(parseFloat(e.target.value || "0") * 100),
                        };
                        setDraft({ ...draft, entries });
                      }}
                      className="w-28 border dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-900 dark:text-gray-100"
                    />
                    <button
                      onClick={() =>
                        setDraft({ ...draft, entries: draft.entries.filter((_, j) => j !== i) })
                      }
                      className="text-red-600 text-xs px-1"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setDraft(null)} className="px-4 py-2 text-sm border dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300">
                Cancel
              </button>
              <button
                onClick={create}
                disabled={working || !draft.name}
                className="px-4 py-2 text-sm bg-brand text-white rounded-lg disabled:opacity-50"
              >
                {working ? "Saving…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CardEditor({
  card,
  label,
  working,
  onSave,
  onDelete,
}: {
  card: RateCard;
  label: string;
  working: boolean;
  onSave: (entries: Entry[]) => void;
  onDelete: () => void;
}) {
  const [entries, setEntries] = useState<Entry[]>(card.entries);
  const dirty = JSON.stringify(entries) !== JSON.stringify(card.entries);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">{card.name}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {label}
            {card.is_default && " · default"}
            {!card.active && " · inactive"}
          </p>
        </div>
        <button onClick={onDelete} className="text-xs text-red-600">
          Delete
        </button>
      </div>
      <table className="w-full text-sm mt-3">
        <tbody>
          {entries.map((en, i) => (
            <tr key={i}>
              <td className="py-1 pr-2">
                <input
                  value={en.role}
                  onChange={(e) => {
                    const next = [...entries];
                    next[i] = { ...en, role: e.target.value };
                    setEntries(next);
                  }}
                  className="w-full border dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-900 dark:text-gray-100 text-xs"
                />
              </td>
              <td className="py-1 w-28">
                <input
                  type="number"
                  step="0.01"
                  value={(en.hourly_rate_cents / 100).toString()}
                  onChange={(e) => {
                    const next = [...entries];
                    next[i] = {
                      ...en,
                      hourly_rate_cents: Math.round(parseFloat(e.target.value || "0") * 100),
                    };
                    setEntries(next);
                  }}
                  className="w-full border dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-900 dark:text-gray-100 text-xs font-mono"
                />
              </td>
              <td className="py-1 w-6 text-right">
                <button
                  onClick={() => setEntries(entries.filter((_, j) => j !== i))}
                  className="text-red-500 text-xs"
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-between mt-2">
        <button
          onClick={() => setEntries([...entries, { role: "", hourly_rate_cents: 0, sort_index: entries.length }])}
          className="text-xs text-brand dark:text-brand-light"
        >
          + Add role
        </button>
        {dirty && (
          <button
            onClick={() => onSave(entries)}
            disabled={working}
            className="px-3 py-1 text-xs bg-brand text-white rounded-lg disabled:opacity-50"
          >
            Save changes
          </button>
        )}
      </div>
      {!dirty && entries.length > 0 && (
        <p className="text-[10px] text-gray-400 mt-2 font-mono">
          {entries.map((e) => `${e.role}: ${fmt(e.hourly_rate_cents)}`).join("  ·  ")}
        </p>
      )}
    </div>
  );
}
